import { useRef } from "react";
import { WistiaPlayer } from "@wistia/wistia-player-react";
import {
  actionNames,
  actionPrefixes,
  eventTypes,
  objectTypes,
  useTracker,
} from "@newsela/snitch";

import { formatTime } from "@/view/GeniusContent/utils";
import {
  GenerationGeniusContent,
  VideoSubject,
  WistiaPlayerCustomEvent,
} from "@/view/GeniusContent/GeniusContent.interface";
import {
  geniusFigureCss,
  geniusVideoWrapperCss,
  playerColor,
} from "./GeniusVideo.style";

interface GeniusVideoProps {
  wistiaId: string;
  content: GenerationGeniusContent;
  setDuration?: (_: string) => void;
  isVideoOnly?: boolean;
  isPreview?: boolean;
}

const GeniusVideo = ({
  wistiaId,
  content,
  setDuration,
  isVideoOnly = false,
  isPreview = false,
}: GeniusVideoProps) => {
  const { id: contentId, page_viewmodel: pageViewmodel } = content;
  const { trackAction, getEventName } = useTracker();
  const handleReady = ({ target }: WistiaPlayerCustomEvent) => {
    const { duration: videoDuration } = target;
    setDuration?.(formatTime(videoDuration));
  };
  const firstPlayRef = useRef(false);
  const handlePlay = () => {
    if (firstPlayRef.current) return;
    firstPlayRef.current = true;
    trackAction({
      event_type: eventTypes.click,
      event_name: getEventName(
        objectTypes.generationGeniusVideo,
        actionPrefixes.initiate,
        actionNames.start,
      ),
      dimension_properties: {
        description: "Playing generation genius video on wistia",
        content_id: contentId,
        content_type: "GenerationGeniusVideo",
      },
    });
  };

  const subject =
    // fallback to SCIENCE branding if subject is undefined
    (pageViewmodel?.["subject"] as VideoSubject) || VideoSubject.SCIENCE;

  return (
    <div
      css={geniusVideoWrapperCss(subject)}
      data-testid="genius-video-wrapper"
      {...(isPreview && { inert: "" })}
    >
      <figure css={geniusFigureCss(isVideoOnly)}>
        <WistiaPlayer
          data-testid="wistia-player"
          mediaId={wistiaId}
          onPlay={handlePlay}
          onCanPlay={handleReady}
          onCanPlayThrough={handleReady}
          playerColor={playerColor(subject)}
          controlsVisibleOnLoad={!isPreview}
          resumable={!isPreview}
        />
      </figure>
    </div>
  );
};

export default GeniusVideo;
